import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus } from 'lucide-react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import ArcGISMap from '../../components/ArcGISMap';
import SuccessModal from '../../components/modals/SuccessModal';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserManagementContext } from '../../contexts/UserManagementContext';
import { deviceApi, DeviceType } from '../../services/deviceApi';
import {
  extractModelLetterFromSerial,
  matchDeviceTypeByModelLetter,
  validateSerialNumber,
  parseSerialNumber
} from '../../config/deviceModels';
import '../../styles/pages/bulk-import-devices.css';
import '../../styles/pages/alerts.css';

type ImportStage = 'upload' | 'processing' | 'location' | 'summary';

type BulkDeviceStatus = 'pending' | 'processing' | 'success' | 'failed' | 'duplicate' | 'invalid';

interface BulkDevice {
  serialNumber: string;
  deviceName: string;
  deviceTypeId: number;
  deviceTypeName: string;
  status: BulkDeviceStatus;
  error?: string;
  latitude?: number | null;
  longitude?: number | null;
  installationLocation?: string;
  locationType?: 'indoor' | 'outdoor';
  siteId?: number | null;
}

interface LocationPayload {
  latitude: number;
  longitude: number;
  installationLocation: string;
  locationType: 'indoor' | 'outdoor';
  siteId: number | null;
}

const LOCATION_OPTIONS: string[] = [
  'Living Room',
  'Bedroom',
  'Kitchen',
  'Dining Room',
  'Bathroom',
  "Kid's Room",
  'Conference Room',
  'Open Space',
  'Office Space',
  'School Classroom',
  'Hospital Room',
  'Warehouse Space',
  'Other',
];

const SERIAL_HEADERS = ['serial number', 'serialnumber', 'serial_no', 'serial', 'serialnumber*'];

const BulkImportDevicesPage: React.FC = () => {
  const navigate = useNavigate();
  const devicesContext = useContext(DevicesContext);

  if (!devicesContext) {
    throw new Error('BulkImportDevicesPage must be used within a DevicesProvider');
  }

  const { createDevice } = devicesContext;

  const userMgmtContext = useContext(UserManagementContext);
  const { sites = [], getSites, createSite } = userMgmtContext || {};

  useEffect(() => { getSites?.(); }, []);

  const [showAddSiteForm, setShowAddSiteForm] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteLocation, setNewSiteLocation] = useState('');
  const [creatingSite, setCreatingSite] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [stage, setStage] = useState<ImportStage>('upload');
  const [deviceRows, setDeviceRows] = useState<BulkDevice[]>([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState<number>(-1);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationDraft, setLocationDraft] = useState<LocationPayload | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isCreatingDevice, setIsCreatingDevice] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    duplicates: 0,
    invalid: 0,
  });
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        setLoadingDeviceTypes(true);
        const types = await deviceApi.getDeviceTypes();
        setDeviceTypes(types);
      } catch (error) {
        console.error('Failed to load device types', error);
        setUploadError('Failed to load device types. Please try again later.');
      } finally {
        setLoadingDeviceTypes(false);
      }
    };

    fetchDeviceTypes();
  }, []);

  const handleBackClick = () => {
    navigate('/add-device');
  };

  const determineDeviceType = useCallback(
    (serialNumber: string): { typeId: number; typeName: string; autoName?: string } | null => {
      if (!serialNumber) return null;

      const cleanSerial = serialNumber.replace(/\s+/g, '').toUpperCase();

      // Try new SEI format first (SEI100C784250001)
      if (validateSerialNumber(cleanSerial).valid) {
        const parsedSerial = parseSerialNumber(cleanSerial);
        if (parsedSerial) {
          const matchedType = matchDeviceTypeByModelLetter(parsedSerial.modelLetter, deviceTypes);
          if (matchedType) {
            // Auto-generate device name: "<DeviceType> <DeviceID>" (e.g. "CORE 0001")
            const autoName = `${matchedType.description} ${parsedSerial.deviceId}`;
            console.log(`✅ Bulk Import - Auto-detected: ${matchedType.description} (Model: ${parsedSerial.modelLetter}) → ${autoName}`);
            return { typeId: matchedType.id, typeName: matchedType.typeName, autoName };
          }
        }

        // Fallback: just model-letter match without deviceId
        const modelLetter = extractModelLetterFromSerial(cleanSerial);
        if (modelLetter) {
          const matchedType = matchDeviceTypeByModelLetter(modelLetter, deviceTypes);
          if (matchedType) {
            return { typeId: matchedType.id, typeName: matchedType.typeName };
          }
        }
      }

      // Fallback to legacy format matching for old devices
      const serialNumberLower = serialNumber.toLowerCase().trim();
      for (const type of deviceTypes) {
        if (serialNumberLower.includes(type.typeName.toLowerCase())) {
          console.log(`ℹ️ Bulk Import - Legacy match: ${type.description}`);
          return { typeId: type.id, typeName: type.typeName };
        }
      }

      return null;
    },
    [deviceTypes]
  );

  const parseSerialFromRow = (row: Record<string, any>): string | null => {
    const keys = Object.keys(row);
    for (const header of SERIAL_HEADERS) {
      const matchingKey = keys.find(key => key.toLowerCase().trim() === header);
      if (matchingKey && row[matchingKey]) {
        return String(row[matchingKey]).trim();
      }
    }

    // Fallback to first column value
    if (keys.length > 0) {
      const value = row[keys[0]];
      if (value) {
        return String(value).trim();
      }
    }

    return null;
  };

  const resetFlow = () => {
    setStage('upload');
    setDeviceRows([]);
    setCurrentDeviceIndex(-1);
    setIsLocationModalOpen(false);
    setLocationDraft(null);
    setProcessingProgress(0);
    setIsCreatingDevice(false);
    setShowSuccessModal(false);
    setSummaryStats({
      total: 0,
      success: 0,
      failed: 0,
      duplicates: 0,
      invalid: 0,
    });
    setUploadError(null);
  };

  const handleFile = async (file: File) => {
    if (!file) return;
    if (loadingDeviceTypes) {
      setUploadError('Device types are still loading. Please wait a moment and try again.');
      return;
    }
    if (!deviceTypes.length) {
      setUploadError('No device types available. Please contact an administrator.');
      return;
    }

    try {
      setUploadError(null);
      setStage('processing');
      setProcessingProgress(10);

      const buffer = await file.arrayBuffer();
      setProcessingProgress(35);
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      if (!worksheet) {
        throw new Error('Unable to read the uploaded file. Please check the file format.');
      }

      // Read as raw arrays so the first row isn't auto-treated as a header
      const rawRows = XLSX.utils.sheet_to_json<any[]>(worksheet, {
        defval: '',
        raw: false,
        blankrows: false,
        header: 1,
      });
      setProcessingProgress(55);

      if (!rawRows.length) {
        throw new Error('No data found in the uploaded file.');
      }

      // Detect if first row is a header (contains a known serial-column header) and skip it.
      const firstRowFirstCell = rawRows[0]?.[0] != null ? String(rawRows[0][0]).trim().toLowerCase() : '';
      const isFirstRowHeader = SERIAL_HEADERS.includes(firstRowFirstCell);
      const dataRows = isFirstRowHeader ? rawRows.slice(1) : rawRows;

      // Convert each raw row to a record keyed by the first column so existing parser works.
      const rows: Record<string, any>[] = dataRows.map(arr => ({ 'serial number': arr?.[0] ?? '' }));

      const seenSerials = new Set<string>();
      const parsedDevices: BulkDevice[] = [];
      let duplicates = 0;
      let invalid = 0;

      for (const row of rows) {
        const serialNumber = parseSerialFromRow(row);
        if (!serialNumber) {
          invalid += 1;
          parsedDevices.push({
            serialNumber: '',
            deviceName: '',
            deviceTypeId: 0,
            deviceTypeName: '',
            status: 'invalid',
            error: 'Missing serial number',
          });
          continue;
        }

        if (seenSerials.has(serialNumber)) {
          duplicates += 1;
          parsedDevices.push({
            serialNumber,
            deviceName: `Device ${serialNumber}`,
            deviceTypeId: 0,
            deviceTypeName: '',
            status: 'duplicate',
            error: 'Duplicate serial number in file',
          });
          continue;
        }

        const detectedType = determineDeviceType(serialNumber);
        if (!detectedType) {
          // Surface the specific reason (bad model letter, country code, year, etc.)
          // so users can fix each row without guessing.
          const validation = validateSerialNumber(serialNumber);
          invalid += 1;
          parsedDevices.push({
            serialNumber,
            deviceName: `Device ${serialNumber}`,
            deviceTypeId: 0,
            deviceTypeName: '',
            status: 'invalid',
            error: validation.error || 'Unable to determine device type from serial number',
          });
          continue;
        }

        seenSerials.add(serialNumber);
        parsedDevices.push({
          serialNumber,
          deviceName: detectedType.autoName || `Device ${serialNumber}`,
          deviceTypeId: detectedType.typeId,
          deviceTypeName: detectedType.typeName,
          status: 'pending',
        });
      }

      const total = parsedDevices.length;
      setDeviceRows(parsedDevices);
      setSummaryStats(prev => ({
        ...prev,
        total,
        duplicates,
        invalid,
      }));
      setProcessingProgress(100);

      if (total === duplicates + invalid) {
        const plural = (n: number, s: string) => `${n} ${s}${n === 1 ? '' : 's'}`;
        let message: string;
        if (invalid > 0 && duplicates > 0) {
          message = `None of the ${plural(total, 'row')} could be imported: ${plural(invalid, 'row')} had invalid serial numbers and ${plural(duplicates, 'row')} ${duplicates === 1 ? 'was a duplicate' : 'were duplicates'}. Please fix the file and try again.`;
        } else if (invalid > 0) {
          message = `None of the ${plural(total, 'row')} could be imported — ${invalid === 1 ? 'the serial number is invalid' : 'all serial numbers are invalid'}. Expected format: SEI100C784250001. Please correct the file and try again.`;
        } else {
          message = `All ${plural(total, 'row')} contain duplicate serial numbers. Please remove the duplicates and try again.`;
        }
        setUploadError(message);
        setStage('upload');
        return;
      }

      setStage('location');
    } catch (error: any) {
      console.error('Bulk import parsing error:', error);
      setUploadError(error.message || 'Failed to process the uploaded file.');
      setStage('upload');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFile(event.dataTransfer.files[0]);
      event.dataTransfer.clearData();
    }
  };

  const pendingDeviceIndex = useMemo(() => {
    return deviceRows.findIndex(device => device.status === 'pending');
  }, [deviceRows]);

  useEffect(() => {
    if (stage === 'location') {
      if (pendingDeviceIndex !== -1) {
        setCurrentDeviceIndex(pendingDeviceIndex);
        const device = deviceRows[pendingDeviceIndex];
        setLocationDraft({
          latitude: device.latitude ?? 0,
          longitude: device.longitude ?? 0,
          installationLocation: device.installationLocation ?? '',
          locationType: device.locationType ?? 'indoor',
          siteId: device.siteId ?? null,
        });
        setIsLocationModalOpen(true);
      } else {
        setIsLocationModalOpen(false);
        setStage('summary');
      }
    }
  }, [stage, pendingDeviceIndex, deviceRows]);

  const updateDeviceRow = useCallback(
    (index: number, updater: (_device: BulkDevice) => BulkDevice) => {
      setDeviceRows(prev => prev.map((row, i) => (i === index ? updater(row) : row)));
    },
    []
  );

  const handleLocationSelect = (lat: number, lng: number) => {
    setLocationDraft(prev => {
      if (!prev) {
        return {
          latitude: lat,
          longitude: lng,
          installationLocation: '',
          locationType: 'indoor',
          siteId: null,
        };
      }
      return { ...prev, latitude: lat, longitude: lng };
    });
  };

  const handleLocationFieldChange = (
    field: keyof LocationPayload,
    value: string | number | 'indoor' | 'outdoor' | null
  ) => {
    setLocationDraft(prev => {
      const draft: LocationPayload = prev ?? {
        latitude: 0,
        longitude: 0,
        installationLocation: '',
        locationType: 'indoor',
        siteId: null,
      };
      return {
        ...draft,
        [field]: value,
      } as LocationPayload;
    });
  };

  const handleSaveLocation = async () => {
    if (currentDeviceIndex === -1 || !locationDraft) return;
    if (
      locationDraft.latitude === null ||
      locationDraft.latitude === undefined ||
      locationDraft.longitude === null ||
      locationDraft.longitude === undefined ||
      locationDraft.latitude === 0 ||
      locationDraft.longitude === 0
    ) {
      setUploadError('Please select a location on the map.');
      return;
    }
    if (!locationDraft.installationLocation.trim()) {
      setUploadError('Please enter the location name.');
      return;
    }
    if (locationDraft.siteId === null || locationDraft.siteId === undefined) {
      setUploadError('Please select a site.');
      return;
    }
    setUploadError(null);

    setIsCreatingDevice(true);

    // If "Apply to All" is checked, process all pending devices
    if (applyToAll) {
      const pendingDevices = deviceRows
        .map((device, index) => ({ device, index }))
        .filter(({ device }) => device.status === 'pending');

      for (const { device, index } of pendingDevices) {
        updateDeviceRow(index, d => ({ ...d, status: 'processing' }));

        const payload = {
          DeviceTypeId: device.deviceTypeId,
          DeviceName: device.deviceName,
          SerialNumber: device.serialNumber,
          DeviceLocationType: locationDraft.locationType,
          InstallationLocation: locationDraft.installationLocation,
          Description: '',
          Latitude: locationDraft.latitude,
          Longitude: locationDraft.longitude,
          SiteId: locationDraft.siteId,
        };

        try {
          const response: any = await createDevice(payload);
          // Treat DEVICE_NOT_CONNECTED as success — the device IS registered, just not yet connected
          const isRegistered = response?.success || response?.eventCode === 'DEVICE_NOT_CONNECTED';
          if (isRegistered) {
            updateDeviceRow(index, d => ({
              ...d,
              status: 'success',
              latitude: locationDraft.latitude,
              longitude: locationDraft.longitude,
              installationLocation: locationDraft.installationLocation,
              locationType: locationDraft.locationType,
              siteId: locationDraft.siteId,
              error: response?.eventCode === 'DEVICE_NOT_CONNECTED' ? response?.message : undefined,
            }));
            setSummaryStats(prev => ({
              ...prev,
              success: prev.success + 1,
            }));
          } else {
            throw new Error(response?.message || 'Failed to create device');
          }
        } catch (error: any) {
          console.error('Failed to create device from bulk import', error);
          updateDeviceRow(index, d => ({
            ...d,
            status: 'failed',
            error: error.message || 'Failed to create device',
          }));
          setSummaryStats(prev => ({
            ...prev,
            failed: prev.failed + 1,
          }));
        }
      }

      setIsCreatingDevice(false);
      setIsLocationModalOpen(false);
      setApplyToAll(false);
      return;
    }

    // Single device processing
    const currentDevice = deviceRows[currentDeviceIndex];
    updateDeviceRow(currentDeviceIndex, device => ({
      ...device,
      status: 'processing',
    }));

    const payload = {
      DeviceTypeId: currentDevice.deviceTypeId,
      DeviceName: currentDevice.deviceName,
      SerialNumber: currentDevice.serialNumber,
      DeviceLocationType: locationDraft.locationType,
      InstallationLocation: locationDraft.installationLocation,
      Description: '',
      Latitude: locationDraft.latitude,
      Longitude: locationDraft.longitude,
      SiteId: locationDraft.siteId,
    };

    try {
      const response: any = await createDevice(payload);
      // Treat DEVICE_NOT_CONNECTED as success — the device IS registered, just not yet connected
      const isRegistered = response?.success || response?.eventCode === 'DEVICE_NOT_CONNECTED';
      if (isRegistered) {
        updateDeviceRow(currentDeviceIndex, device => ({
          ...device,
          status: 'success',
          latitude: locationDraft.latitude,
          longitude: locationDraft.longitude,
          installationLocation: locationDraft.installationLocation,
          locationType: locationDraft.locationType,
          siteId: locationDraft.siteId,
          error: response?.eventCode === 'DEVICE_NOT_CONNECTED' ? response?.message : undefined,
        }));
        setSummaryStats(prev => ({
          ...prev,
          success: prev.success + 1,
        }));
      } else {
        throw new Error(response?.message || 'Failed to create device');
      }
    } catch (error: any) {
      console.error('Failed to create device from bulk import', error);
      updateDeviceRow(currentDeviceIndex, device => ({
        ...device,
        status: 'failed',
        error: error.message || 'Failed to create device',
      }));
      setSummaryStats(prev => ({
        ...prev,
        failed: prev.failed + 1,
      }));
    } finally {
      setIsCreatingDevice(false);
      setIsLocationModalOpen(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setIsLocationModalOpen(false);
  };

  useEffect(() => {
    if (!showSuccessModal && stage === 'location' && !isLocationModalOpen && !isCreatingDevice) {
      const nextIndex = deviceRows.findIndex(device => device.status === 'pending');
      if (nextIndex !== -1) {
        setCurrentDeviceIndex(nextIndex);
        const device = deviceRows[nextIndex];
        setLocationDraft({
          latitude: device.latitude ?? 0,
          longitude: device.longitude ?? 0,
          installationLocation: device.installationLocation ?? '',
          locationType: device.locationType ?? 'indoor',
          siteId: device.siteId ?? null,
        });
        setIsLocationModalOpen(true);
      } else if (deviceRows.length > 0) {
        setStage('summary');
      }
    }
  }, [showSuccessModal, stage, isLocationModalOpen, isCreatingDevice, deviceRows]);

  const handleSuccessModalAction = () => {
    handleSuccessModalClose();
  };

  const handleViewDevices = () => {
    navigate('/devices');
  };

  const renderStageContent = () => {
    if (stage === 'processing') {
      return (
        <div className="bulk-import-processing-card">
          <div className="bulk-import-processing-header">
            <div className="bulk-import-processing-icon" />
            <h4>Processing File</h4>
          </div>
          <p>We&apos;re validating your data. This may take a few seconds.</p>
          <div className="bulk-import-progress-bar">
            <div
              className="bulk-import-progress-fill"
              style={{ width: `${processingProgress}%` }}
            />
          </div>
          <span className="bulk-import-progress-value">{processingProgress}%</span>
          <button type="button" className="bulk-import-back-button" onClick={resetFlow}>
            Back to Options
          </button>
        </div>
      );
    }

    if (stage === 'summary') {
      return null;
    }

    if (stage === 'location') {
      const pendingDevices = deviceRows.filter(device => device.status === 'pending');
      const processingDevices = deviceRows.filter(device => device.status === 'processing');

      return (
        <div className="bulk-import-location-stage">
          <div className="bulk-import-location-header">
            <h4>Assign Locations</h4>
            <p>Select the location for each device to complete the import.</p>
          </div>

          <div className="bulk-import-device-list">
            {deviceRows.map((device, index) => (
              <div key={`${device.serialNumber}-${index}`} className="bulk-import-device-item">
                <div>
                  <p className="bulk-import-device-name">{device.deviceName || 'Unnamed Device'}</p>
                  <p className="bulk-import-device-serial">{device.serialNumber}</p>
                  <p className="bulk-import-device-type">{device.deviceTypeName}</p>
                  {device.error && <p className="bulk-import-device-error">{device.error}</p>}
                </div>
                <div className={`bulk-import-status bulk-import-status-${device.status}`}>
                  {device.status === 'pending' && 'Pending'}
                  {device.status === 'processing' && 'Saving'}
                  {device.status === 'success' && 'Completed'}
                  {device.status === 'failed' && 'Failed'}
                  {device.status === 'duplicate' && 'Duplicate'}
                  {device.status === 'invalid' && 'Invalid'}
                </div>
              </div>
            ))}
          </div>

          <div className="bulk-import-location-footer">
            <span>
              Pending: {pendingDevices.length} | Saving: {processingDevices.length}
            </span>
            <div className="bulk-import-location-actions">
              <button type="button" className="bulk-import-secondary-btn" onClick={resetFlow}>
                Cancel Import
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        <div
          className="bulk-import-dropzone"
          onDragOver={event => event.preventDefault()}
          onDrop={handleDrop}
        >
          <img 
            src={`${process.env.PUBLIC_URL}/SVGIcons/BulkDownload.svg`} 
            alt="Bulk Import" 
            className="bulk-import-icon-img" 
          />
          <h4>Bulk Import Devices</h4>
          <p>Upload a CSV or Excel file to import multiple devices at once</p>
          <div className="bulk-import-drop-area">
            <div className="bulk-import-drop-outline" />
            <div className="bulk-import-drop-content">
              <svg 
                className="bulk-import-upload-icon" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M12 15V3m0 0L8 7m4-4l4 4M2 17l.621 2.485A2 2 0 004.561 21h14.878a2 2 0 001.94-1.515L22 17" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              <p>Drop your file here, or click to browse</p>
              <span>Supports CSV and Excel files (max 10MB)</span>
              <label htmlFor="bulk-import-input" className="bulk-import-primary-btn">
                Choose File
                <input
                  id="bulk-import-input"
                  type="file"
                  accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        </div>
        <p className="bulk-import-helper-text">
          Template expectations: Provide a column named &quot;Serial Number&quot; with device serials.
          Device name and type will be inferred automatically.
        </p>
        <button type="button" className="bulk-import-back-to-options-btn" onClick={handleBackClick}>
          Back To Options
        </button>
      </>
    );
  };

  const currentDevice =
    currentDeviceIndex >= 0 && currentDeviceIndex < deviceRows.length
      ? deviceRows[currentDeviceIndex]
      : null;

  return (
    <DashboardLayout>
      <div className={`bulk-import-container${screenSize <= 768 && (isLocationModalOpen || stage === 'summary') ? ' isMobileModalOpen' : ''}`}>
        <div className="add-device-header mb-2">
          <div className="add-device-header-title">
            <button
              onClick={handleBackClick}
              className="back-button zoomOutLevelForDeviceInfoCard"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="back-icon">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h3 className="device-title">Add New Device</h3>
          </div>
          <p className="device-subtitle mobile-subtitle md:ml-14">
            Add a new SmartEco device to your dashboard.
          </p>
        </div>

        {uploadError && <div className="bulk-import-alert">{uploadError}</div>}

        <div className="bulk-import-body">{renderStageContent()}</div>
      </div>

      {currentDevice && isLocationModalOpen && locationDraft && (
        <div className="modal-overlay zoomOutLevel" onClick={() => { if (!isCreatingDevice) { setIsLocationModalOpen(false); resetFlow(); } }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="modal-header">
              <div className="flex md:items-start items-center md:flex-col flex-row">
                <button
                  type="button"
                  onClick={() => { setIsLocationModalOpen(false); resetFlow(); }}
                  className="md:hidden block mr-3"
                  disabled={isCreatingDevice}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="back-icon">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <div>
                  <h2 className="modal-title">Location Details</h2>
                  <p className="bulk-import-modal-sub-title">
                    Device Serial Number: <strong>{currentDevice.serialNumber}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setIsLocationModalOpen(false); resetFlow(); }}
                className="modal-close"
                disabled={isCreatingDevice}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form className="modal-form" onSubmit={e => e.preventDefault()}>
              <div className="arcgis-map-container">
                <ArcGISMap
                  latitude={locationDraft.latitude ?? 0}
                  longitude={locationDraft.longitude ?? 0}
                  onLocationSelect={(lat, lng) => {
                    handleLocationSelect(lat, lng);
                  }}
                />
              </div>
              <div className="bulk-import-location-summary">
                <p>
                  Selected Coordinates:{' '}
                  <span>
                    {locationDraft.latitude?.toFixed(5)}, {locationDraft.longitude?.toFixed(5)}
                  </span>
                </p>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="bulk-import-location-name">Location Type</label>
                <select
                  id="bulk-import-location-name"
                  className="form-input"
                  value={locationDraft.installationLocation}
                  onChange={event =>
                    handleLocationFieldChange('installationLocation', event.target.value)
                  }
                  disabled={isCreatingDevice}
                >
                  <option value="">Select location</option>
                  {LOCATION_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="bulk-import-site">Site</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select
                    id="bulk-import-site"
                    className="form-input"
                    value={locationDraft.siteId ?? ''}
                    onChange={event =>
                      handleLocationFieldChange('siteId', event.target.value ? Number(event.target.value) : null)
                    }
                    disabled={isCreatingDevice || showAddSiteForm}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Site</option>
                    {sites.map(s => (
                      <option key={s.siteId} value={s.siteId}>{s.siteName}</option>
                    ))}
                  </select>
                  {!showAddSiteForm && (
                    <button
                      type="button"
                      className="modal-button secondary"
                      onClick={() => setShowAddSiteForm(true)}
                      disabled={isCreatingDevice}
                      style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.25rem', flex: '0' }}
                    >
                      <Plus size={16} /> Add Site
                    </button>
                  )}
                </div>
                {showAddSiteForm && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Site Name"
                      value={newSiteName}
                      onChange={e => setNewSiteName(e.target.value)}
                      disabled={creatingSite}
                    />
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Site Location"
                      value={newSiteLocation}
                      onChange={e => setNewSiteLocation(e.target.value)}
                      disabled={creatingSite}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="modal-button primary"
                        disabled={creatingSite}
                        onClick={async () => {
                          if (!newSiteName.trim() || !newSiteLocation.trim()) return;
                          setCreatingSite(true);
                          const site = await createSite?.({ siteName: newSiteName.trim(), siteLocation: newSiteLocation.trim() });
                          setCreatingSite(false);
                          if (site) {
                            handleLocationFieldChange('siteId', site.siteId);
                            setNewSiteName('');
                            setNewSiteLocation('');
                            setShowAddSiteForm(false);
                          }
                        }}
                      >
                        {creatingSite ? 'Saving...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        className="modal-button secondary"
                        disabled={creatingSite}
                        onClick={() => { setShowAddSiteForm(false); setNewSiteName(''); setNewSiteLocation(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={applyToAll}
                    onChange={e => setApplyToAll(e.target.checked)}
                    disabled={isCreatingDevice}
                  />
                  <span>Apply this location to all remaining devices</span>
                </label>
              </div>
            </form>

            {/* Footer */}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-button secondary"
                onClick={() => { setIsLocationModalOpen(false); resetFlow(); }}
                disabled={isCreatingDevice}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button primary"
                onClick={handleSaveLocation}
                disabled={isCreatingDevice}
              >
                {isCreatingDevice ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {stage === 'summary' && (
        <div className="modal-overlay zoomOutLevel">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="modal-header">
              <div className="flex md:items-start items-center md:flex-col flex-row">
                <button type="button" onClick={resetFlow} className="md:hidden block mr-3">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="back-icon">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <h2 className="modal-title">Import Summary</h2>
                <p className="modal-subtitle md:block hidden">Review the results of your bulk device import.</p>
              </div>
              <button type="button" onClick={resetFlow} className="modal-close">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="modal-form">
              <div style={{ textAlign: 'center' }}>
                <div className="bulk-import-summary-icon" />
                <h4 className='modal-title'>Import Complete!</h4>
                <p className='bulk-import-modal-sub-title'>You&apos;re all done. Your devices have been created.</p>
              </div>
              <div className="bulk-import-summary-stats">
                <div className="bulk-import-summary-pill">
                  <span className="bulk-import-summary-value">{summaryStats.total}</span>
                  <span className="bulk-import-summary-label">Total</span>
                </div>
                <div className="bulk-import-summary-pill">
                  <span className="bulk-import-summary-value">{summaryStats.success}</span>
                  <span className="bulk-import-summary-label">Imported</span>
                </div>
                <div className="bulk-import-summary-pill">
                  <span className="bulk-import-summary-value">{summaryStats.failed}</span>
                  <span className="bulk-import-summary-label">Failed</span>
                </div>
                <div className="bulk-import-summary-pill">
                  <span className="bulk-import-summary-value">{summaryStats.duplicates}</span>
                  <span className="bulk-import-summary-label">Duplicates</span>
                </div>
              </div>

              {deviceRows.length > 0 && (
                <div className="bulk-import-summary-details">
                  <h5 className="bulk-import-summary-details-title">Device Details</h5>
                  <div className="bulk-import-summary-details-list">
                    {deviceRows.map((d, i) => (
                      <div key={`${d.serialNumber}-${i}`} className={`bulk-import-summary-detail bulk-import-status-${d.status}`}>
                        <div className="bulk-import-summary-detail-header">
                          <span className="bulk-import-summary-detail-serial">{d.serialNumber || 'Unknown'}</span>
                          <span className={`bulk-import-status bulk-import-status-${d.status}`}>
                            {d.status === 'success' && 'Registered'}
                            {d.status === 'failed' && 'Failed'}
                            {d.status === 'duplicate' && 'Duplicate'}
                            {d.status === 'invalid' && 'Invalid'}
                            {d.status === 'pending' && 'Pending'}
                          </span>
                        </div>
                        {d.error && <p className="bulk-import-summary-detail-message">{d.error}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-actions">
              <button type="button" className="modal-button secondary" onClick={resetFlow}>
                Back to Options
              </button>
              <button type="button" className="modal-button primary" onClick={handleViewDevices}>
                View All Devices
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Successfully added location details"
        subtitle="You can continue to capture details for each device from the next step."
        actionText="Continue"
        onAction={handleSuccessModalAction}
      />
    </DashboardLayout>
  );
};

export default BulkImportDevicesPage;

